import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import { collection, getDocs, addDoc, serverTimestamp } from "firebase/firestore";
import { db } from "../firebase";
import AddComment from "./AddComment";
import EditableText from "./EditableText";
import { editForumPost, deleteForumPost, editForumComment, deleteForumComment } from "../utils/forumActions";
import { notifyAllUsers } from "../utils/notificationHelpers";
import { newestFirst, oldestFirst } from "../utils/time";
import { toast } from "react-hot-toast";
import genericProfilePic from "../genericprofilepic.png";

function DiscussionRoom(props) {
  const userId = localStorage.getItem("user_id");

  const starterpost = {
    user_id: userId,
    discussion_room: props.discussionroom,
    post: "",
  };

  const [post, setPost] = useState(starterpost);
  const [visiblePosts, setVisiblePosts] = useState([]);
  const [users, setUsers] = useState([]);
  const [comments, setComments] = useState([]);
  const [openCommentFor, setOpenCommentFor] = useState(null);
  const [posting, setPosting] = useState(false);

  const handleEditChange = (e) => {
    setPost({
      ...post,
      [e.target.name]: e.target.value,
    });
  };

  const fetchData = async () => {
    try {
      const [postsSnap, commentsSnap, usersSnap] = await Promise.all([
        getDocs(collection(db, 'posts')),
        getDocs(collection(db, 'postComments')),
        getDocs(collection(db, 'users')),
      ]);
      setVisiblePosts(postsSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort(newestFirst));
      setComments(commentsSnap.docs.map(d => ({ id: d.id, ...d.data() })).sort(oldestFirst));
      setUsers(usersSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error("Failed to load discussion room:", err);
    }
  };

  useEffect(() => {
    fetchData();
  }, [props.discussionroom]); // eslint-disable-line react-hooks/exhaustive-deps

  const postThePost = async (e) => {
    e.preventDefault();
    if (!post.post.trim() || posting) return;
    setPosting(true);
    try {
      await addDoc(collection(db, 'posts'), {
        user_id: userId,
        discussion_room: props.discussionroom,
        post: post.post.trim(),
        created_at: serverTimestamp(),
      });
    } catch (err) {
      console.error("Failed to post:", err);
      toast.error("Could not post. Please try again.");
      setPosting(false);
      return;
    }
    toast.success("Posted!");

    const fromName = `${props.first_name || ''} ${props.last_name || ''}`.trim() || 'Someone';
    const snippet = post.post.length > 80 ? post.post.slice(0, 80) + '…' : post.post;
    notifyAllUsers({
      fromUserId: userId,
      fromName,
      type: 'forum_post',
      message: `${fromName} posted in ${props.title}: "${snippet}"`,
      url: `/${props.discussionroom}`,
    }).catch(() => {});

    setPost({
      user_id: userId,
      discussion_room: props.discussionroom,
      post: "",
    });
    await fetchData();
    setPosting(false);
  };

  const filteredPosts = visiblePosts.filter(
    (p) => p.discussion_room === props.discussionroom
  );

  return (
    <div id={props.bg}>
      <br />
      <br />
      <h2 id={props.titleid}>{props.title}</h2>
      <h4 id={props.subtitleid}>Discussion Room</h4>

      <div id="discussion-room-container">
        <div id="discussionroom-box">
          <div id="startconversationbox">
            <h3 id="startconversation">Start a Conversation</h3>
            <form onSubmit={postThePost} id="postform">
              <textarea
                name="post"
                form="postform"
                value={post.post}
                onChange={handleEditChange}
              ></textarea>
              <button id="submit" type="submit" disabled={posting || !post.post.trim()}>
                {posting ? "Posting…" : "Post"}
              </button>
            </form>
          </div>
          <div>
            {filteredPosts.map((post) =>
              users.map((user) => {
                if (user.id === post.user_id) {
                  return (
                    <div id="postandcomments" key={post.id}>
                      <div className="commentary-text">
                        <div>
                          <img
                            id="mycommentarypic"
                            src={user.profile_pic_url || genericProfilePic}
                            alt={`${user.first_name} ${user.last_name}'s profile`}
                          />
                        </div>
                        <b>
                          {user.first_name} {user.last_name} Wrote:
                        </b>
                      </div>
                      <EditableText
                    text={post.post}
                    edited={!!post.edited_at}
                    canEdit={post.user_id === userId}
                    canDelete={post.user_id === userId}
                    onSave={async ({ text }) => {
                      await editForumPost(post.id, text);
                      setVisiblePosts(prev => prev.map(p => p.id === post.id ? { ...p, post: text, edited_at: new Date() } : p));
                    }}
                    onDelete={async () => {
                      const replyIds = comments.filter(c => c.post_id === post.id).map(c => c.id);
                      await deleteForumPost(post.id, replyIds);
                      setVisiblePosts(prev => prev.filter(p => p.id !== post.id));
                      setComments(prev => prev.filter(c => c.post_id !== post.id));
                    }}
                  />
                      {openCommentFor !== post.id ? (
                        <button
                          className="respond-btn"
                          onClick={() => setOpenCommentFor(post.id)}
                        >
                          Respond
                        </button>
                      ) : (
                        <AddComment
                          setViewAddComment={() => setOpenCommentFor(null)}
                          post_id={post.id}
                          discussionroom={post.discussion_room}
                          post_author_id={post.user_id}
                          onCommentPosted={(newComment) =>
                            setComments(prev => [...prev, newComment])
                          }
                        />
                      )}

                      <div id="comments">
                        <br />
                        <br />
                        <h4 id="comments-title">Comments</h4>
                        {comments
                          .filter((comment) => post.id === comment.post_id)
                          .map((comment) =>
                            users.map((commentuser) => {
                              if (commentuser.id === comment.user_id) {
                                return (
                                  <div key={comment.id}>
                                    <div>
                                      <img
                                        id="smallprofilepic"
                                        src={commentuser.profile_pic_url || genericProfilePic}
                                        alt={`${commentuser.first_name} ${commentuser.last_name}'s profile`}
                                      />
                                    </div>
                                    <p className="commentary-text">
                                      <b>
                                        {commentuser.first_name}{" "}
                                        {commentuser.last_name} Responded:
                                      </b>
                                    </p>
                                    <EditableText
                                      text={comment.comment}
                                      edited={!!comment.edited_at}
                                      itemLabel="comment"
                                      canEdit={comment.user_id === userId}
                                      canDelete={comment.user_id === userId || post.user_id === userId}
                                      onSave={async ({ text }) => {
                                        await editForumComment(comment.id, text);
                                        setComments(prev => prev.map(c => c.id === comment.id ? { ...c, comment: text, edited_at: new Date() } : c));
                                      }}
                                      onDelete={async () => {
                                        await deleteForumComment(comment.id);
                                        setComments(prev => prev.filter(c => c.id !== comment.id));
                                      }}
                                    />
                                    <br />
                                    <br />
                                  </div>
                                );
                              }
                              return null;
                            })
                          )}
                      </div>
                    </div>
                  );
                }
                return null;
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

const mapStateToProps = (state) => {
  return {
    first_name: state.first_name,
    last_name: state.last_name,
    profile_pic_url: state.profile_pic_url,
    profile_pic_alt: state.profile_pic_alt,
  };
};

export default connect(mapStateToProps, {})(DiscussionRoom);
